// src/components/Navbar.jsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, Linkedin, Facebook, Youtube, Instagram, LogIn, LogOut, UserCircle } from 'lucide-react';

const Navbar = ({ onCreateClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleLogin = () => {
    navigate('/login');
    setMobileMenuOpen(false);
  };

  const goToAdmin = () => {
    navigate('/admin');
    setMobileMenuOpen(false);
  };

  return (
    <nav className="bg-[#b8d4d4] sticky top-0 z-50 shadow-md">
      <div className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          {/* Logo - Clickable to go to home */}
          <div className="flex items-center cursor-pointer group" onClick={scrollToTop}>
            <div className="flex flex-col leading-tight">
              {/* OmniFlair on top - letter spaced */}
              <div 
                className="text-2xl md:text-3xl font-black tracking-[0.1em] group-hover:opacity-80 transition-opacity uppercase" 
                style={{ fontFamily: "'Diplomata', cursive" }}
              >
                <span className="text-gray-900">Omni</span>
                <span className="text-orange-500">Flair</span>
              </div>
              {/* EVENTS below - same letter spacing with left padding */}
              <div 
                className="text-sm md:text-base font-bold text-gray-700 tracking-[1.5em] group-hover:opacity-80 transition-opacity uppercase pl-3" 
                style={{ fontFamily: "'Hepta Slab', serif" }}
              >
                EVENTS
              </div>
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center overflow-hidden rounded-lg">
            <a href="#home" className="bg-[#f4d8a8] text-gray-800 px-4 py-3 text-sm font-medium transition-all hover:bg-[#e8c994]" style={{ fontFamily: "'Hepta Slab', serif" }}>Home</a>
            <a href="#about" className="bg-[#d4d4d4] text-gray-800 px-4 py-3 text-sm font-medium transition-all hover:bg-[#c4c4c4]" style={{ fontFamily: "'Hepta Slab', serif" }}>Know Us</a>
            <a href="#services" className="bg-[#d4d4d4] text-gray-800 px-4 py-3 text-sm font-medium transition-all hover:bg-[#c4c4c4]" style={{ fontFamily: "'Hepta Slab', serif" }}>Our Services</a>
            <a href="#gallery" className="bg-[#d4d4d4] text-gray-800 px-4 py-3 text-sm font-medium transition-all hover:bg-[#c4c4c4]" style={{ fontFamily: "'Hepta Slab', serif" }}>Sneak Peak</a>
            <a href="#blog" className="bg-[#d4d4d4] text-gray-800 px-4 py-3 text-sm font-medium transition-all hover:bg-[#c4c4c4]" style={{ fontFamily: "'Hepta Slab', serif" }}>Blog</a>
            <a href="#contact" className="bg-[#d4d4d4] text-gray-800 px-4 py-3 text-sm font-medium transition-all hover:bg-[#c4c4c4]" style={{ fontFamily: "'Hepta Slab', serif" }}>Contact</a>
          </div>

          {/* Desktop Auth Buttons & Social Icons */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Social Icons */}
            <div className="flex items-center space-x-3 border-r border-gray-400 pr-4">
              <a href="https://www.linkedin.com/company/omniflair-events/?viewAsMember=true" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-orange-500 transition-colors duration-200">
                <Linkedin size={22} />
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-orange-500 transition-colors duration-200">
                <Facebook size={22} />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-orange-500 transition-colors duration-200">
                <Youtube size={22} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-orange-500 transition-colors duration-200">
                <Instagram size={22} />
              </a>
            </div>

            {/* Auth Buttons */}
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-white/50 rounded-lg">
                  <UserCircle className="w-5 h-5 text-orange-500" />
                  <span className="text-sm font-bold text-gray-800">{user?.name}</span>
                </div>
                {user?.role === 'admin' && (
                  <button
                    onClick={goToAdmin}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600 transition-colors text-sm"
                  >
                    Admin
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg font-bold hover:bg-red-600 transition-colors flex items-center gap-2 text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-bold hover:from-orange-600 hover:to-orange-700 transition-all flex items-center gap-2 text-sm"
              >
                <LogIn className="w-4 h-4" />
                Login
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button className="lg:hidden p-2 text-gray-800 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 space-y-2 border-t border-gray-400 pt-4">
            <a href="#home" className="block text-gray-800 hover:text-orange-500 hover:bg-[#f4d8a8] px-4 py-2 rounded transition-colors font-medium" onClick={() => setMobileMenuOpen(false)} style={{ fontFamily: "'Hepta Slab', serif" }}>Home</a>
            <a href="#about" className="block text-gray-800 hover:text-orange-500 hover:bg-gray-200 px-4 py-2 rounded transition-colors font-medium" onClick={() => setMobileMenuOpen(false)} style={{ fontFamily: "'Hepta Slab', serif" }}>Know Us</a>
            <a href="#services" className="block text-gray-800 hover:text-orange-500 hover:bg-gray-200 px-4 py-2 rounded transition-colors font-medium" onClick={() => setMobileMenuOpen(false)} style={{ fontFamily: "'Hepta Slab', serif" }}>Our Services</a>
            <a href="#gallery" className="block text-gray-800 hover:text-orange-500 hover:bg-gray-200 px-4 py-2 rounded transition-colors font-medium" onClick={() => setMobileMenuOpen(false)} style={{ fontFamily: "'Hepta Slab', serif" }}>Sneak Peak</a>
            <a href="#blog" className="block text-gray-800 hover:text-orange-500 hover:bg-gray-200 px-4 py-2 rounded transition-colors font-medium" onClick={() => setMobileMenuOpen(false)} style={{ fontFamily: "'Hepta Slab', serif" }}>Blog</a>
            <a href="#contact" className="block text-gray-800 hover:text-orange-500 hover:bg-gray-200 px-4 py-2 rounded transition-colors font-medium" onClick={() => setMobileMenuOpen(false)} style={{ fontFamily: "'Hepta Slab', serif" }}>Contact</a>

            {/* Mobile Auth Section */}
            <div className="border-t border-gray-400 pt-4 mt-4">
              {isAuthenticated ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/50 rounded-lg">
                    <UserCircle className="w-5 h-5 text-orange-500" />
                    <span className="text-sm font-bold text-gray-800">{user?.name}</span>
                    <span className="text-xs bg-orange-500 text-white px-2 py-1 rounded-full">{user?.role}</span>
                  </div>
                  {user?.role === 'admin' && (
                    <button
                      onClick={goToAdmin}
                      className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600 transition-colors text-sm"
                    >
                      Go to Admin Dashboard
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 bg-red-500 text-white rounded-lg font-bold hover:bg-red-600 transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleLogin}
                  className="w-full px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-bold hover:from-orange-600 hover:to-orange-700 transition-all flex items-center justify-center gap-2 text-sm"
                >
                  <LogIn className="w-4 h-4" />
                  Login
                </button>
              )}
            </div>

            {/* Mobile Social Icons */}
            <div className="flex items-center justify-center space-x-4 pt-4 border-t border-gray-400 mt-4">
              <a href="https://www.linkedin.com/company/omniflair-events/?viewAsMember=true" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-orange-500">
                <Linkedin size={24} />
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-orange-500">
                <Facebook size={24} />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-orange-500">
                <Youtube size={24} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-orange-500">
                <Instagram size={24} />
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
